/** Opening a door (PRD §5.1). Split from the api.ts monolith 2026-08-03; the barrel re-exports. */
import { call, get, post } from "./core";
import { hashConsent } from "./plan";
// --- POST /api/v1/door/create (PRD §5.1) -----------------------------------

export type Severity = "hard" | "soft";
export type Exclusion = { text: string; severity: Severity };

export type DoorCreateInput = {
  gets_through: string[];
  doesnt: Exclusion[];
  timezone: string;
};

export type DoorCreated = {
  /** No hearing charge: a new door has no price to be created with (economics-wire §6.4). */
  door: { handle: string; status: string; public_url: string };
  house_rules: { version: number; markdown: string };
};

export type DoorCreateResult =
  | { ok: true; created: DoorCreated }
  | {
      ok: false;
      code: "UNAUTHENTICATED" | "REGULATED_CATEGORY" | "UNKNOWN";
    };

type RawCreate = {
  door?: Partial<DoorCreated["door"]>;
  house_rules?: Partial<DoorCreated["house_rules"]>;
  error?: { code?: string };
};

export type HouseRules = DoorCreated["house_rules"];

export async function createDoor(input: DoorCreateInput): Promise<DoorCreateResult> {
  const res = await post("/api/v1/door/create", input);
  const body = (await res.json().catch(() => ({}))) as RawCreate;

  if (!res.ok) {
    const code = body.error?.code;
    if (res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    if (code === "REGULATED_CATEGORY") return { ok: false, code: "REGULATED_CATEGORY" };
    return { ok: false, code: "UNKNOWN" };
  }

  const handle = body.door?.handle ?? "";
  return {
    ok: true,
    created: {
      door: {
        handle,
        status: body.door?.status ?? "live",
        public_url: body.door?.public_url ?? `https://nob.me/@${handle}`,
      },
      house_rules: {
        version: body.house_rules?.version ?? 1,
        markdown: body.house_rules?.markdown ?? "",
      },
    },
  };
}

// --- the wizard's own state and its three writes (ruling C5) ----------------

/**
 * WHERE THE RUN IS, and it is never a step number (PRD FR-1.2 #7).
 *
 * Three flags, all of them the server's, and the screen picks a step from
 * them — which is what lets somebody who did Connect and Plan on a phone open a
 * laptop and land on the Rules step with nothing to restore.
 */
export type OnboardingState = {
  /**
   * The mailbox has not proved itself yet (ruling R-0826-3). The one step in
   * the run with no way past it: everything Nob is obliged to write reads that
   * one address.
   */
  email_pending: boolean;
  /** The trial screen has an offer to make, no plan is on, and it has not been passed (§2.2.4). */
  perk_pending: boolean;
  /** A door exists. Every account has one from the email step on (ruling R-1006-2). */
  has_door: boolean;
  /** The Rules step (tuning the door) has not completed; true while it is deferred too. */
  rules_pending: boolean;
  /** The door step was stepped aside from deliberately (§2.3.3). */
  defer_rules: boolean;
  /** A door lives at an X handle, so an email knocker owes no door step. */
};

/**
 * The words the door step renders — the chips, the two dials' paragraphs, and
 * the line for choosing nothing.
 *
 * THE CLIENT COMPOSES NONE OF IT. Every sentence here is what gets written into
 * `nob.md`, so it is authored once, on the server, and travels; a copy of a
 * rule in this bundle would be a second rule, drifting from the first.
 */
export type StarterVocabulary = {
  interests: Array<{ id: string; label: string; sentence: string }>;
  no_interests: string;
  filter_text: Record<string, string>;
  notify_text: Record<string, string>;
};

/**
 * What is standing on the other side of the door being hung (owner item 3).
 *
 * NOT A FLAG, which is why it is not in `OnboardingState`: the flags say where
 * the run is and this says what the run is worth finishing for. The VERIFIED
 * count and nothing else — the same figure the public waiting room at this
 * handle already shows a passer-by, so an owner's screen never discloses more
 * than the page anybody can read (WR H7, FR-G7.1). No lines, no positions, no
 * registrants: the lines are the conversion preview, which is AI work.
 */
export type OnboardingDemand = { count: number };

export type OnboardingView = {
  onboarding: OnboardingState;
  starter: StarterVocabulary;
  waiting: OnboardingDemand;
  /**
   * The age attestation, in the server's words (ruling R-0914-1 (1)).
   *
   * It travels for the same reason the starter vocabulary does: the screen
   * shows these exact bytes and posts the SHA-256 of them, and a copy compiled
   * into this bundle would be a second wording that nobody could prove somebody
   * agreed to. Empty string when the payload could not be read — the step then
   * cannot be answered, which is the right way for this to fail.
   */
  age_attestation: string;
};

export type FilterLevel = "easy" | "medium" | "hard";
export type NotifyLevel = "less" | "medium" | "more";

export type DoorStepAnswers = {
  interests: string[];
  filter_level: FilterLevel;
  notify_level: NotifyLevel;
};

export type OnboardingFailureCode = "UNAUTHENTICATED" | "VALIDATION_ERROR" | "UNKNOWN";

export type OnboardingResult<T> = ({ ok: true } & T) | { ok: false; code: OnboardingFailureCode };

const ONBOARDING_OFFLINE = { ok: false, code: "UNKNOWN" } as const;

function flagsOf(raw: unknown): OnboardingState {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    // Defaults FALSE across the board: a payload that could not be read must
    // not park somebody on a step, and the console is the safe landing.
    email_pending: o["email_pending"] === true,
    perk_pending: o["perk_pending"] === true,
    has_door: o["has_door"] === true,
    rules_pending: o["rules_pending"] === true,
    defer_rules: o["defer_rules"] === true,
  };
}

function demandCountOf(raw: unknown): number {
  const w = (raw ?? {}) as Record<string, unknown>;
  const n = w["count"];
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function starterOf(raw: unknown): StarterVocabulary {
  const s = (raw ?? {}) as Record<string, unknown>;
  const interests: StarterVocabulary["interests"] = [];
  if (Array.isArray(s["interests"])) {
    for (const item of s["interests"]) {
      const i = (item ?? {}) as Record<string, unknown>;
      if (typeof i["id"] === "string" && typeof i["label"] === "string") {
        interests.push({
          id: i["id"],
          label: i["label"],
          sentence: typeof i["sentence"] === "string" ? i["sentence"] : "",
        });
      }
    }
  }
  const texts = (value: unknown): Record<string, string> => {
    const out: Record<string, string> = {};
    if (value && typeof value === "object") {
      for (const [k, v] of Object.entries(value)) if (typeof v === "string") out[k] = v;
    }
    return out;
  };
  return {
    interests,
    no_interests: typeof s["no_interests"] === "string" ? s["no_interests"] : "",
    filter_text: texts(s["filter_text"]),
    notify_text: texts(s["notify_text"]),
  };
}

function onboardingFailure(
  status: number,
  body: unknown,
): { ok: false; code: OnboardingFailureCode } {
  const code = (body as { error?: { code?: string } })?.error?.code;
  if (status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (code === "VALIDATION_ERROR") return { ok: false, code: "VALIDATION_ERROR" };
  return ONBOARDING_OFFLINE;
}

export async function fetchOnboarding(): Promise<OnboardingResult<{ view: OnboardingView }>> {
  const r = await call(() => get("/api/v1/onboarding"));
  if (!r) return ONBOARDING_OFFLINE;
  if (!r.res.ok) return onboardingFailure(r.res.status, r.body);
  const b = r.body as Record<string, unknown>;
  return {
    ok: true,
    view: {
      onboarding: flagsOf(b["onboarding"]),
      starter: starterOf(b["starter"]),
      // Zero is the safe default for the same reason the flags default false:
      // a payload that could not be read must not make a claim about how many
      // people are waiting, and the line is absent at zero.
      waiting: { count: demandCountOf(b["waiting"]) },
      age_attestation: typeof b["age_attestation"] === "string" ? b["age_attestation"] : "",
    },
  };
}

// --- the email step, and the account page's change (ruling R-0826-3) -------

/**
 * ONE MACHINERY, TWO ENTRY POINTS. The wizard's Email step and the Account
 * page's "Change" both call these, because naming the address and moving it are
 * the same act — and an unproved second address is exactly the failure the
 * first proof was for.
 *
 * The server's own refusal is carried verbatim where it wrote one: the address
 * rules belong to the API, and a client that paraphrased them would eventually
 * paraphrase them wrong.
 */
export type EmailRequestResult =
  | { ok: true; to: string; resend_in_seconds: number }
  | { ok: false; code: "TOO_SOON"; retry_after_seconds: number }
  | {
      ok: false;
      code:
        | "UNAUTHENTICATED"
        | "VALIDATION_ERROR"
        | "MAIL_UNAVAILABLE"
        | "AGE_ATTESTATION_REQUIRED"
        | "ATTESTATION_MISMATCH"
        | "UNKNOWN";
      message: string | null;
    };

/**
 * The address, and — on the wizard's step only — the age attestation with it
 * (ruling R-0914-1 (1)).
 *
 * `attestation` is the server's own sentence, arrived on the run state. The
 * TEXT does not go back: only its SHA-256, hashed exactly as a purchase consent
 * is (`hashConsent`), so the server can say whether the screen showed what it
 * authored. The account page's Change passes nothing, because nobody ticked
 * anything there and a consent the client posts on somebody's behalf is not one.
 */
export async function requestEmailCode(
  email: string,
  attestation?: string,
): Promise<EmailRequestResult> {
  const consent =
    attestation && attestation.length > 0
      ? { age_attested: true, age_attestation_hash: await hashConsent(attestation) }
      : {};
  const r = await call(() =>
    post("/api/v1/onboarding/email/request", { email: email.trim(), ...consent }),
  );
  if (!r) return { ok: false, code: "UNKNOWN", message: null };
  const body = (r.body ?? {}) as {
    to?: unknown;
    resend_in_seconds?: unknown;
    error?: { code?: string; message?: string; details?: Record<string, unknown> };
  };
  if (!r.res.ok) {
    const code = body.error?.code;
    if (r.res.status === 429 || code === "TOO_SOON") {
      const wait = body.error?.details?.["retry_after_seconds"];
      return {
        ok: false,
        code: "TOO_SOON",
        // A throttle that cannot say how long is a throttle the screen has to
        // guess at; the floor is the server's own and the fallback is it.
        retry_after_seconds: typeof wait === "number" && wait > 0 ? Math.ceil(wait) : 60,
      };
    }
    // The FIELD's own refusal first, then the request's. A validation failure
    // arrives as a list of issues, and the one the person can act on is the one
    // attached to the field they typed in.
    const issues = body.error?.details?.["issues"];
    const first =
      Array.isArray(issues) && issues[0] && typeof issues[0] === "object"
        ? (issues[0] as { message?: unknown }).message
        : undefined;
    const message =
      typeof first === "string" && first.trim()
        ? first.trim()
        : typeof body.error?.message === "string" && body.error.message.trim()
          ? body.error.message.trim()
          : null;
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED", message };
    if (code === "MAIL_UNAVAILABLE") return { ok: false, code: "MAIL_UNAVAILABLE", message };
    if (code === "AGE_ATTESTATION_REQUIRED") {
      return { ok: false, code: "AGE_ATTESTATION_REQUIRED", message };
    }
    if (code === "ATTESTATION_MISMATCH")
      return { ok: false, code: "ATTESTATION_MISMATCH", message };
    if (r.res.status === 400) return { ok: false, code: "VALIDATION_ERROR", message };
    return { ok: false, code: "UNKNOWN", message };
  }
  return {
    ok: true,
    // Masked by the server (`d***@example.com`): enough for the owner to
    // recognise which mailbox to look in, not enough for a borrowed session to
    // learn a new fact.
    to: typeof body.to === "string" ? body.to : "",
    resend_in_seconds: typeof body.resend_in_seconds === "number" ? body.resend_in_seconds : 60,
  };
}

export type EmailConfirmResult =
  | { ok: true; contact_email: string; state: OnboardingState }
  | { ok: false; code: "WRONG_CODE"; attempts_left: number }
  | {
      ok: false;
      code: "UNAUTHENTICATED" | "EXPIRED" | "LOCKED_OUT" | "NO_PENDING" | "UNKNOWN";
    };

export async function confirmEmailCode(code: string): Promise<EmailConfirmResult> {
  const r = await call(() => post("/api/v1/onboarding/email/confirm", { code }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  const body = (r.body ?? {}) as {
    contact_email?: unknown;
    onboarding?: unknown;
    error?: { code?: string; details?: Record<string, unknown> };
  };
  if (!r.res.ok) {
    if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
    const code = body.error?.code;
    if (code === "WRONG_CODE") {
      const left = body.error?.details?.["attempts_left"];
      return {
        ok: false,
        code: "WRONG_CODE",
        attempts_left: typeof left === "number" && left > 0 ? left : 0,
      };
    }
    if (code === "EXPIRED" || code === "LOCKED_OUT" || code === "NO_PENDING") {
      return { ok: false, code };
    }
    return { ok: false, code: "UNKNOWN" };
  }
  return {
    ok: true,
    contact_email: typeof body.contact_email === "string" ? body.contact_email : "",
    state: flagsOf(body.onboarding),
  };
}

/** The onboarding trial screen, passed. The offer itself is untouched (ruling R-1006-2). */
export async function dismissTrialPrompt(): Promise<OnboardingResult<{ state: OnboardingState }>> {
  const r = await call(() => post("/api/v1/onboarding/trial/dismiss", {}));
  if (!r) return ONBOARDING_OFFLINE;
  if (!r.res.ok) return onboardingFailure(r.res.status, r.body);
  return { ok: true, state: flagsOf((r.body as Record<string, unknown>)["onboarding"]) };
}

/** The door step, owed rather than skipped (§2.3.3). */
export async function deferDoorStep(): Promise<OnboardingResult<{ state: OnboardingState }>> {
  const r = await call(() => post("/api/v1/onboarding/defer", {}));
  if (!r) return ONBOARDING_OFFLINE;
  if (!r.res.ok) return onboardingFailure(r.res.status, r.body);
  return { ok: true, state: flagsOf((r.body as Record<string, unknown>)["onboarding"]) };
}

/**
 * The door step completing — it tunes the door the email step hung (ruling R-1006-2). The
 * answers go up as ids and levels; the sentences they become are the server's.
 */
export async function completeDoorStep(
  answers: DoorStepAnswers,
): Promise<OnboardingResult<{ handle: string; state: OnboardingState }>> {
  const r = await call(() => post("/api/v1/onboarding/door", answers));
  if (!r) return ONBOARDING_OFFLINE;
  if (!r.res.ok) return onboardingFailure(r.res.status, r.body);
  const b = r.body as Record<string, unknown>;
  const door = (b["door"] ?? {}) as Record<string, unknown>;
  return {
    ok: true,
    handle: typeof door["handle"] === "string" ? door["handle"] : "",
    state: flagsOf(b["onboarding"]),
  };
}
